import { formatINR } from '@/utils/format';

interface CashFlowCardProps {
  totalRevenue?: number;
  totalPurchases?: number;
}

export default function CashFlowCard({ totalRevenue = 0, totalPurchases = 0 }: CashFlowCardProps) {
  const totalReceipts = totalRevenue;
  const totalPayments = totalPurchases;
  const netCashFlow = totalReceipts - totalPayments;

  const weeklyReceipt = totalReceipts / 4;
  const weeklyPayment = totalPayments / 4;

  const weekly = [
    { week: 'Week 1', receipts: Math.round(weeklyReceipt * 0.9), payments: Math.round(weeklyPayment * 1.1) },
    { week: 'Week 2', receipts: Math.round(weeklyReceipt * 1.1), payments: Math.round(weeklyPayment * 0.9) },
    { week: 'Week 3', receipts: Math.round(weeklyReceipt * 1.2), payments: Math.round(weeklyPayment * 0.8) },
    { week: 'Week 4', receipts: Math.round(weeklyReceipt * 0.8), payments: Math.round(weeklyPayment * 1.2) },
  ];

  const maxVal = Math.max(1, ...weekly.flatMap((w) => [w.receipts, w.payments]));

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-[#1e293b] mb-1">Cash Flow</h3>
      <p className="text-xs text-[#64748b] mb-4">In period — weekly breakdown</p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2.5 bg-emerald-50 rounded-lg">
          <p className="text-xs text-emerald-600 font-medium">Receipts</p>
          <p className="text-sm font-bold text-emerald-700 mt-0.5">{formatINR(totalReceipts)}</p>
        </div>
        <div className="text-center p-2.5 bg-red-50 rounded-lg">
          <p className="text-xs text-red-600 font-medium">Payments</p>
          <p className="text-sm font-bold text-red-700 mt-0.5">{formatINR(totalPayments)}</p>
        </div>
        <div className={`text-center p-2.5 rounded-lg ${netCashFlow >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <p className={`text-xs font-medium ${netCashFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Net Cash</p>
          <p className={`text-sm font-bold mt-0.5 ${netCashFlow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {formatINR(Math.abs(netCashFlow))}
          </p>
        </div>
      </div>

      {/* Weekly bars */}
      <div className="space-y-2">
        {weekly.map((w) => {
          const recPct = Math.round((w.receipts / maxVal) * 100);
          const payPct = Math.round((w.payments / maxVal) * 100);
          return (
            <div key={w.week} className="flex items-center gap-2">
              <span className="text-xs text-[#94a3b8] w-12 flex-shrink-0">{w.week}</span>
              <div className="flex-1 flex flex-col gap-0.5">
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${Math.max(recPct, 2)}%` }} />
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-400 rounded-full" style={{ width: `${Math.max(payPct, 2)}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-3">
        <span className="flex items-center gap-1.5 text-xs text-[#64748b]">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-400 inline-block" />
          Receipts
        </span>
        <span className="flex items-center gap-1.5 text-xs text-[#64748b]">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-400 inline-block" />
          Payments
        </span>
      </div>
    </div>
  );
}
